import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { type Control, type UseFormReturn, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';

import type { CalculationData } from '~/api/common/types';
import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';
import type { Product } from '~/api/products/types';

import { useInvoiceTemplateCalculationData } from '~/api/invoiceTemplates';
import { groupLinesInFormSections } from '~/features/documents';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import {
  useDefaultBrandId,
  useDefaultClientAndContactId,
  useDefaultCondition,
  useDefaultCustomAgreement,
  useDefaultCustomCondition,
  useDefaultNumberingSeriesId,
  useDefaultSpecialConditions,
} from '~/hooks/useDefaultFormValues';
import { removeFormDataFromSession, retrieveFormDataFromSession } from '~/utils/form';

import type { InvoiceTemplateForm } from './CreateEditInvoiceTemplateForm.types';

import { mapInvoiceTemplateFormDataToPayload } from './CreateEditInvoiceTemplateForm.utils';

export const useInvoiceTemplateDefaultValues = (invoiceTemplate: InvoiceTemplate | null): InvoiceTemplateForm => {
  const brandId = useDefaultBrandId(invoiceTemplate?.brandId ?? null);
  const numberingSeriesId = useDefaultNumberingSeriesId(invoiceTemplate?.numberingSeriesId ?? null, 'invoice');
  const { clientId, contactId } = useDefaultClientAndContactId(invoiceTemplate?.clientId ?? null, invoiceTemplate?.contactId ?? null);
  const condition = useDefaultCondition(invoiceTemplate?.conditionId ?? null);
  const customCondition = useDefaultCustomCondition(invoiceTemplate?.customConditionId ?? null);
  const customAgreement = useDefaultCustomAgreement(invoiceTemplate?.customAgreementId ?? null, contactId);
  const specialConditions = useDefaultSpecialConditions(invoiceTemplate?.specialConditions ?? null, contactId, customAgreement?.id ?? null);

  return {
    numberingSeriesId,
    brandId,
    clientId,
    contactId,
    condition: customAgreement ?? customCondition ?? condition,
    title: invoiceTemplate?.title ?? '',
    description: invoiceTemplate?.description ?? '',
    paymentTerm: invoiceTemplate?.paymentTerm ?? 30,
    paymentTermEndOfMonth: invoiceTemplate?.paymentTermEndOfMonth ?? false,
    interval: invoiceTemplate?.interval ?? null,
    nextInvoiceAt: invoiceTemplate?.nextInvoiceAt ? dayjs(invoiceTemplate?.nextInvoiceAt).toDate() : null,
    clientReference: invoiceTemplate?.clientReference ?? '',
    specialConditions,
    sections: invoiceTemplate !== null ? groupLinesInFormSections(invoiceTemplate.lines) : [{ lines: [], isPhotoAlbum: false }],
  };
};

export const useInvoiceTemplatePersistedFormValues = (form: UseFormReturn<InvoiceTemplateForm>) => {
  const location = useLocation();
  const updatedProduct = (location.state?.updatedProduct || null) as Product | null;

  useEffect(() => {
    try {
      const persistedFormData = retrieveFormDataFromSession(window.location.pathname) as InvoiceTemplateForm | null;

      if (persistedFormData === null) return;

      form.setValue('title', persistedFormData.title);
      form.setValue('numberingSeriesId', persistedFormData.numberingSeriesId);
      form.setValue('clientId', persistedFormData.clientId);
      form.setValue('contactId', persistedFormData.contactId);
      form.setValue('brandId', persistedFormData.brandId);
      form.setValue('paymentTerm', persistedFormData.paymentTerm);
      form.setValue('paymentTermEndOfMonth', persistedFormData.paymentTermEndOfMonth);
      form.setValue('interval', persistedFormData.interval);
      form.setValue('nextInvoiceAt', persistedFormData.nextInvoiceAt);
      form.setValue('description', persistedFormData.description);
      form.setValue('clientReference', persistedFormData.clientReference);
      form.setValue('condition', persistedFormData.condition);
      form.setValue('specialConditions', persistedFormData.specialConditions);
      form.setValue(
        'sections',
        // When a product has been edited, apply the updates to all lines that use it (because the persisted lines are stale).
        updatedProduct !== null
          ? persistedFormData.sections.map((section) => ({
              ...section,
              lines: section.lines.map((line) =>
                line.type === 'entry' && line.productId === updatedProduct.id
                  ? {
                      ...line,
                      activityType: updatedProduct.activityType,
                      price: updatedProduct.price ?? 0,
                      unit: updatedProduct.unit,
                    }
                  : line,
              ),
            }))
          : persistedFormData.sections,
      );
    } finally {
      removeFormDataFromSession(window.location.pathname);
    }
  }, [form, updatedProduct]);
};

export const useCalculationData = (invoiceTemplate: InvoiceTemplate | null, control: Control<InvoiceTemplateForm>) => {
  const formData = useWatch({ control }) as InvoiceTemplateForm;
  const [debouncedData] = useDebouncedState(formData, 100);

  const { data, isFetching } = useInvoiceTemplateCalculationData(
    invoiceTemplate ?? null,
    mapInvoiceTemplateFormDataToPayload(debouncedData),
  );

  return useMemo(
    () => ({
      calculationData: data ?? invoiceTemplate?.calculationData ?? defaultCalculationData,
      isFetching,
    }),
    [data, invoiceTemplate?.calculationData, isFetching],
  );
};

const defaultCalculationData = {
  advantageWithCs: null,
  advantageWithoutCs: null,
  coDiscount: 0,
  coExclVat: 0,
  coInclVat: 0,
  coVat: 0,
  coVatAmounts: [
    { percentage: 6, amount: 0 },
    { percentage: 21, amount: 0 },
  ],
  fcCopyrightExclVat: 0,
  fcExclVat: 0,
  fcPerformanceExclVat: 0,
  fcVatAmounts: [
    { percentage: 6, amount: 0 },
    { percentage: 21, amount: 0 },
  ],
  fcWithholdingTax: 0,
  isMinimumCommissionApplied: false,
  userIsWorkingFullTime: true,
  clientIsExemptFromVat: false,
} satisfies CalculationData;
